// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Linking, Modal, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors, { alpha } from '@/theme/colors';
import appFonts from '@/theme/typography';
import { MlDatasetsPanel } from '@/features/course-trace/MlDatasetsPanel';
import { MlModelsPanel, describeHyperparams } from '@/features/course-trace/MlModelsPanel';
import { MlModelVotePanel } from '@/features/course-trace/MlModelVotePanel';
import { ClaudeCheckPanel } from '@/features/course-trace/ClaudeCheckPanel';
import { MlThresholdProfilesPanel } from '@/features/course-trace/MlThresholdProfilesPanel';
import { describeFilter, pickBySize } from '@/features/course-trace/MlDatasetsPanel';
import { useMlModels, type MlDataset, type MlModel } from '@/features/course-trace/useMlModels';
import {
  useMlCourseSets,
  type CourseSetWriteResult,
  type MlCourseSet,
  type MlCourseSetDraft,
  type MlCourseSetPurpose,
  type MlCourseSets,
} from '@/features/ml/useMlCourseSets';
import type { MlBattery } from '@/features/ml/mlBattery';
import {
  useMlJobs,
  useMlTrainingData,
  type LoadState,
  type MlJob,
  type MlTrainingCourse,
  type MlThresholds,
} from '@/features/ml/useMlTraining';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** The three tools `training/` ships, in the order they are run. */
const TOOLS = ['extract', 'train', 'evaluate', 'tune'] as const;
type Tool = (typeof TOOLS)[number];

/** Providers the pipeline knows. The list is a fallback — what is stored wins (see `providers`). */
const KNOWN_PROVIDERS = ['lantmateriet-orto', 'esri-worldimagery'];

/** The parts of the page, each behind its own button and each opening in its own window. */
type Section = 'run' | 'data' | 'sets' | 'jobs' | 'datasets' | 'models' | 'thresholds' | 'votes' | 'claude';

/** The kinds a label count is shown for, in the order the detector cares about them. */
const LABEL_KINDS = ['bunker', 'fairway', 'tee', 'green', 'water', 'trees'];

/**
 * Everything about the detection models in one developer screen: what has been trained, what
 * there is left to train on, and the button that queues a run.
 *
 * It exists because the answer to "can we train on Lantmateriet imagery yet" used to require
 * reading `training/data/manifest.json` on somebody's laptop and running the extractor's own
 * SQL by hand. Data — not code — has been the bottleneck the whole time, so the training-set
 * half of this page is the important half: it names every course that clears the richness
 * floor, every course that misses it and by how much, and links straight to the OpenStreetMap
 * element and the stored imagery each one would be cut from.
 *
 * Developer-or-admin gated, the same bar as the batch fleet (see BFF
 * GeodataEndpoints.ResolveDeveloperCaller): pipeline detail, not course data.
 */
export default function MlTrainingScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { state: data, refresh: refreshData } = useMlTrainingData(apiBaseUrl, user?.token, canView);
  const { state: jobs, refresh: refreshJobs, queue } = useMlJobs(apiBaseUrl, user?.token, canView);
  const { state: sets, save: saveSet, remove: removeSet } = useMlCourseSets(apiBaseUrl, user?.token, canView);

  const providers = useMemo(() => {
    if (data.status !== 'ready') return KNOWN_PROVIDERS;
    const stored = new Set<string>();
    for (const course of data.courses) {
      for (const image of course.images) stored.add(image.provider);
    }
    // Whatever is actually stored, then anything known that is not — so a provider with no
    // imagery yet can still be picked for an extract run that will go and fetch it.
    return [...stored, ...KNOWN_PROVIDERS.filter((p) => !stored.has(p))];
  }, [data]);

  // One section is open at a time, and none of them is open on arrival. The page used to stack
  // all five on top of each other, so the training-set list — the long one, and the one the page
  // exists for — started a screen and a half below the fold.
  const [openSection, setOpenSection] = useState<Section | null>(null);
  const closeSection = () => setOpenSection(null);

  // The registry — models and datasets — asked for once, and only once a window is open: the
  // sets window builds sizes from a dataset's recorded split, the run form names the dataset
  // on a training run and lists the models an evaluation can score. Nothing on the closed page
  // needs it, and the page used to keep that promise per window.
  const { models, datasets } = useMlModels(apiBaseUrl, user?.token, canView && openSection !== null);

  const eligible = data.status === 'ready' ? data.courses.filter((c) => c.eligible).length : null;

  return (
    <>
      <Stack.Screen options={{ title: t('mlTrainingPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('mlTrainingPage.title')}</Text>
        <Text style={styles.intro}>{t('mlTrainingPage.intro')}</Text>

        {!canView ? (
          <Text style={styles.paragraph} testID="ml-training-forbidden">
            {t('mlTrainingPage.forbidden')}
          </Text>
        ) : (
          <>
            {/* Stays on the page rather than inside the run window: whether a queued run starts
                now or waits is worth knowing before deciding to open the form. */}
            <WorkerBanner
              worker={jobs.status !== 'ready' ? 'unknown' : jobs.workerOnline ? 'online' : 'offline'}
              battery={jobs.status === 'ready' ? jobs.workerBattery : null}
              t={t}
            />

            <View style={styles.sectionRow}>
              <SectionButton
                testID="ml-training-section-run"
                label={t('mlTrainingPage.runHeading')}
                detail={t('mlTrainingPage.runDetail')}
                onPress={() => setOpenSection('run')}
              />
              <SectionButton
                testID="ml-training-section-data"
                label={t('mlTrainingPage.dataHeading')}
                detail={
                  data.status === 'ready'
                    ? t('mlTrainingPage.eligibleShort', { eligible: eligible ?? 0, total: data.courses.length })
                    : data.status === 'loading'
                      ? t('mlTrainingPage.loading')
                      : t('mlTrainingPage.unavailable')
                }
                onPress={() => setOpenSection('data')}
              />
              <SectionButton
                testID="ml-training-section-sets"
                label={t('mlTrainingPage.setsHeading')}
                detail={
                  sets.status === 'ready'
                    ? t('mlTrainingPage.setsCount', { count: sets.sets.length })
                    : sets.status === 'loading'
                      ? t('mlTrainingPage.loading')
                      : t('mlTrainingPage.unavailable')
                }
                onPress={() => setOpenSection('sets')}
              />
              <SectionButton
                testID="ml-training-section-jobs"
                label={t('mlTrainingPage.jobsHeading')}
                detail={
                  jobs.status === 'ready'
                    ? t('mlTrainingPage.jobsCount', { count: jobs.jobs.length })
                    : jobs.status === 'loading'
                      ? t('mlTrainingPage.loading')
                      : t('mlTrainingPage.unavailable')
                }
                onPress={() => setOpenSection('jobs')}
              />
              <SectionButton
                testID="ml-training-section-datasets"
                label={t('mlTrainingPage.datasetsHeading')}
                detail={t('mlTrainingPage.datasetsDetail')}
                onPress={() => setOpenSection('datasets')}
              />
              <SectionButton
                testID="ml-training-section-models"
                label={t('mlTrainingPage.modelsHeading')}
                detail={t('mlTrainingPage.modelsDetail')}
                onPress={() => setOpenSection('models')}
              />
              <SectionButton
                testID="ml-training-section-thresholds"
                label={t('mlTrainingPage.thresholdProfilesHeading')}
                detail={t('mlTrainingPage.thresholdProfilesDetail')}
                onPress={() => setOpenSection('thresholds')}
              />
              <SectionButton
                testID="ml-training-section-votes"
                label={t('mlTrainingPage.votesHeading')}
                detail={t('mlTrainingPage.votesDetail')}
                onPress={() => setOpenSection('votes')}
              />
              <SectionButton
                testID="ml-training-section-claude"
                label={t('mlTrainingPage.claudeCheckHeading')}
                detail={t('mlTrainingPage.claudeCheckDetail')}
                onPress={() => setOpenSection('claude')}
              />
            </View>

            <SectionModal
              visible={openSection === 'run'}
              title={t('mlTrainingPage.runHeading')}
              testID="ml-training-run-modal"
              onClose={closeSection}
              t={t}
            >
              <RunPanel
                providers={providers}
                courses={data.status === 'ready' ? data.courses : []}
                sets={sets.status === 'ready' ? sets.sets : []}
                models={models}
                datasets={datasets}
                apiBaseUrl={apiBaseUrl}
                token={user?.token}
                onQueue={queue}
                t={t}
              />
            </SectionModal>

            <SectionModal
              visible={openSection === 'data'}
              title={t('mlTrainingPage.dataHeading')}
              testID="ml-training-data-modal"
              onClose={closeSection}
              t={t}
            >
              <TrainingDataSection data={data} onRefresh={refreshData} t={t} />
            </SectionModal>

            <SectionModal
              visible={openSection === 'sets'}
              title={t('mlTrainingPage.setsHeading')}
              testID="ml-training-sets-modal"
              onClose={closeSection}
              t={t}
            >
              <CourseSetsSection
                sets={sets}
                courses={data.status === 'ready' ? data.courses : []}
                onSave={saveSet}
                datasets={datasets}
                onRemove={removeSet}
                t={t}
              />
            </SectionModal>

            <SectionModal
              visible={openSection === 'jobs'}
              title={t('mlTrainingPage.jobsHeading')}
              testID="ml-training-jobs-modal"
              onClose={closeSection}
              t={t}
            >
              <JobsSection jobs={jobs} onRefresh={refreshJobs} t={t} />
            </SectionModal>

            {/* These panels fetch on mount, and a Modal mounts nothing while it is closed — so
                the registry and the threshold profiles are only asked for once they are looked
                at. Datasets and models come from the same request; opening both makes two. */}
            <SectionModal
              visible={openSection === 'datasets'}
              testID="ml-training-datasets-modal"
              onClose={closeSection}
              t={t}
            >
              <MlDatasetsPanel apiBaseUrl={apiBaseUrl} token={user?.token} />
            </SectionModal>

            <SectionModal
              visible={openSection === 'models'}
              testID="ml-training-models-modal"
              onClose={closeSection}
              t={t}
            >
              {/* The jobs the page already polls: an unscored model reads its own evaluate run
                  out of them to say why it has no stars. */}
              <MlModelsPanel
                apiBaseUrl={apiBaseUrl}
                token={user?.token}
                initiallyOpen
                jobs={jobs.status === 'ready' ? jobs.jobs : null}
              />
            </SectionModal>

            <SectionModal
              visible={openSection === 'thresholds'}
              testID="ml-training-thresholds-modal"
              onClose={closeSection}
              t={t}
            >
              <MlThresholdProfilesPanel apiBaseUrl={apiBaseUrl} token={user?.token} />
            </SectionModal>

            <SectionModal
              visible={openSection === 'votes'}
              testID="ml-training-votes-modal"
              onClose={closeSection}
              t={t}
            >
              <MlModelVotePanel apiBaseUrl={apiBaseUrl} token={user?.token} />
            </SectionModal>

            <SectionModal
              visible={openSection === 'claude'}
              testID="ml-training-claude-modal"
              onClose={closeSection}
              t={t}
            >
              <ClaudeCheckPanel apiBaseUrl={apiBaseUrl} token={user?.token} />
            </SectionModal>
          </>
        )}
      </ScrollView>
    </>
  );
}

/**
 * Whether a queued run starts now or sits in the queue.
 *
 * Three states, not two. Until the job queue has been read this is `unknown` — saying "no ML
 * worker is online" from a request that failed is a claim about the fleet made out of no
 * information, and it contradicted the batch-fleet page, which was reading the same rows.
 */
function WorkerBanner({
  worker,
  battery,
  t,
}: {
  worker: 'online' | 'offline' | 'unknown';
  battery: MlBattery | null;
  t: Translate;
}) {
  if (worker === 'unknown') {
    return (
      <Text style={styles.hint} testID="ml-training-worker-unknown">
        {t('mlTrainingPage.workerUnknown')}
      </Text>
    );
  }
  if (worker === 'offline') {
    // Not an error and not a blocker: the job simply sits in the queue. Said plainly because
    // the ML worker runs off-cloud on a developer's own machine and is usually not running.
    return (
      <Text style={styles.warning} testID="ml-training-worker-offline">
        {t('mlTrainingPage.workerOffline')}
      </Text>
    );
  }
  if (battery?.paused) {
    // Online but holding its run for the battery: "starts straight away" would be false, and a
    // held run otherwise looks exactly like a hung one.
    return (
      <Text style={styles.warning} testID="ml-training-worker-battery-paused">
        {t('mlTrainingPage.workerBatteryPaused', { percent: battery.percent, resume: battery.resumeAtPercent })}
      </Text>
    );
  }
  return (
    <>
      <Text style={styles.ok} testID="ml-training-worker-online">
        {t('mlTrainingPage.workerOnline')}
      </Text>
      {battery ? (
        <Text style={styles.hint} testID="ml-training-worker-battery">
          {t('mlTrainingPage.workerBattery', {
            percent: battery.percent,
            pause: battery.pauseAtPercent,
            resume: battery.resumeAtPercent,
          })}
        </Text>
      ) : null}
    </>
  );
}

/** One section of the page: what it holds, and a line saying how much of it there is. */
function SectionButton({
  label,
  detail,
  onPress,
  testID,
}: {
  label: string;
  detail: string;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable accessibilityRole="button" style={styles.sectionButton} testID={testID} onPress={onPress}>
      <Text style={styles.sectionButtonLabel}>{label}</Text>
      <Text style={styles.sectionButtonDetail}>{detail}</Text>
    </Pressable>
  );
}

/** The window a section opens in: a title, a way out, and a scroller around the content. */
function SectionModal({
  visible,
  title,
  onClose,
  testID,
  t,
  children,
}: {
  visible: boolean;
  /** Omitted by a section whose panel carries its own heading, so the window has only one. */
  title?: string;
  onClose: () => void;
  testID: string;
  t: Translate;
  children: ReactNode;
}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.modalOverlay} onPress={onClose}>
        {/* Swallows the tap so clicking the content itself does not dismiss the window. */}
        <Pressable style={styles.sectionCard} onPress={(event) => event.stopPropagation()}>
          <View style={styles.modalHeader}>
            {title ? <Text style={styles.modalTitle}>{title}</Text> : <View />}
            <Pressable
              testID={`${testID}-close`}
              accessibilityRole="button"
              accessibilityLabel={t('menu.close')}
              hitSlop={8}
              onPress={onClose}
            >
              <Text style={styles.modalClose}>{t('menu.close')}</Text>
            </Pressable>
          </View>
          <ScrollView style={styles.sectionScroll} contentContainerStyle={styles.sectionScrollContent}>
            {children}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/**
 * The training set: every course that clears the richness floor, every course that misses it and
 * by how much, and links to the OpenStreetMap element and the stored imagery each one would be
 * cut from.
 */
function TrainingDataSection({
  data,
  onRefresh,
  t,
}: {
  data: ReturnType<typeof useMlTrainingData>['state'];
  onRefresh: () => void;
  t: Translate;
}) {
  return (
    <>
      <View style={styles.refreshRow}>
        <Text style={styles.hint}>{t('mlTrainingPage.dataHint')}</Text>
        <SmallButton onPress={onRefresh} label={t('mlTrainingPage.refresh')} testID="ml-training-data-refresh" />
      </View>

      {data.status === 'loading' ? (
        <Text style={styles.paragraph}>{t('mlTrainingPage.loading')}</Text>
      ) : data.status === 'forbidden' ? (
        <Text style={styles.paragraph} testID="ml-training-forbidden">
          {t('mlTrainingPage.forbidden')}
        </Text>
      ) : data.status === 'failed' ? (
        <>
          <Text style={styles.paragraph} testID="ml-training-data-failed">
            {t('mlTrainingPage.dataFailed')}
            {data.httpStatus ? ` ${t('mlTrainingPage.httpStatus', { status: data.httpStatus })}` : ''}
          </Text>
          <SmallButton onPress={onRefresh} label={t('mlTrainingPage.retry')} testID="ml-training-data-retry" />
        </>
      ) : data.courses.length === 0 ? (
        <Text style={styles.paragraph} testID="ml-training-no-data">
          {t('mlTrainingPage.noData')}
        </Text>
      ) : (
        <>
          <ThresholdSummary
            thresholds={data.thresholds}
            eligible={data.courses.filter((c) => c.eligible).length}
            total={data.courses.length}
            t={t}
          />
          {data.courses.map((course) => (
            <CourseCard key={course.courseId} course={course} t={t} />
          ))}
        </>
      )}
    </>
  );
}

/** What has been run, newest first, with each run's log behind its own button. */
function JobsSection({
  jobs,
  onRefresh,
  t,
}: {
  jobs: ReturnType<typeof useMlJobs>['state'];
  onRefresh: () => void;
  t: Translate;
}) {
  return (
    <>
      <View style={styles.refreshRow}>
        <Text style={styles.hint}>{t('mlTrainingPage.jobsHint')}</Text>
        <SmallButton onPress={onRefresh} label={t('mlTrainingPage.refresh')} testID="ml-training-jobs-refresh" />
      </View>

      {jobs.status === 'loading' ? (
        <Text style={styles.paragraph}>{t('mlTrainingPage.loading')}</Text>
      ) : jobs.status === 'forbidden' ? (
        <Text style={styles.paragraph} testID="ml-training-forbidden">
          {t('mlTrainingPage.forbidden')}
        </Text>
      ) : jobs.status === 'failed' ? (
        <>
          <Text style={styles.paragraph} testID="ml-training-jobs-failed">
            {t('mlTrainingPage.jobsFailed')}
            {jobs.httpStatus ? ` ${t('mlTrainingPage.httpStatus', { status: jobs.httpStatus })}` : ''}
          </Text>
          <SmallButton onPress={onRefresh} label={t('mlTrainingPage.retry')} testID="ml-training-jobs-retry" />
        </>
      ) : jobs.jobs.length === 0 ? (
        <Text style={styles.paragraph} testID="ml-training-no-jobs">
          {t('mlTrainingPage.noJobs')}
        </Text>
      ) : (
        jobs.jobs.map((job) => <JobCard key={job.jobId} job={job} t={t} />)
      )}
    </>
  );
}

/**
 * The form that queues a run.
 *
 * The argv it will send is shown verbatim before it is sent, because these tools are run from a
 * shell far more often than from here — seeing the same command means the page never becomes a
 * second, differently-behaving way to do the same thing.
 */
function RunPanel({
  providers,
  courses,
  sets,
  models,
  datasets,
  apiBaseUrl,
  token,
  onQueue,
  t,
}: {
  providers: string[];
  /** Every labelled course with its stored images — what an evaluate run can be pointed at. */
  courses: MlTrainingCourse[];
  /** The named course sets: a training set narrows a train run, an evaluation set is what an evaluate run scores. */
  sets: MlCourseSet[];
  /** The registry's models: what an evaluate run can score. */
  models: MlModel[];
  /** The registry's datasets: a train run names the newest one for its provider. */
  datasets: MlDataset[];
  apiBaseUrl: string;
  token: string | null | undefined;
  onQueue: (
    kind: string,
    args: string[]
  ) => Promise<{ ok: boolean; message?: string | null; jobId?: string | null; httpStatus?: number | null }>;
  t: Translate;
}) {
  const [tool, setTool] = useState<Tool>('train');
  const [provider, setProvider] = useState(providers[0] ?? KNOWN_PROVIDERS[0]);
  // Infrared on by default (train.py's own default since 2026-09-13): the band is what
  // separates water from shadow, and every Lantmäteriet crop carries it.
  const [nir, setNir] = useState(true);
  const [register, setRegister] = useState(true);
  const [fromS3, setFromS3] = useState(true);
  // Forty, since train.py's recipe change (C1): the loss was still falling at twenty on every
  // September run, and the runner's throughput now fits forty in a working day.
  const [epochs, setEpochs] = useState('40');
  const [extra, setExtra] = useState('');
  const [modelVersion, setModelVersion] = useState('');
  const [courseIds, setCourseIds] = useState<string[]>([]);
  const [trainSetId, setTrainSetId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const trainSets = useMemo(() => sets.filter((set) => set.purpose === 'train'), [sets]);
  const evalSets = useMemo(() => sets.filter((set) => set.purpose === 'evaluate'), [sets]);
  const trainSet = trainSets.find((set) => set.id === trainSetId) ?? null;
  // The dataset a train run learns from: the newest registered for the provider. Passed as
  // --dataset-id so the model row points at its data and the datasets window can say what was
  // trained on it — which, with the page never passing one, it never could.
  const dataset = useMemo(() => newestDatasetFor(datasets, provider), [datasets, provider]);

  const registered = useMemo(() => models.filter((model) => model.id !== null), [models]);
  const selectedModel = registered.find((model) => model.version === modelVersion) ?? null;
  const params = useMemo(
    () => describeHyperparams(selectedModel?.hyperparamsJson ?? null),
    [selectedModel]
  );
  const holdout = params.holdoutCourses;
  const trained = params.trainCourseIds;
  // A model's holdout is the only honest default: the courses it never saw, which is also the
  // set evaluate.py agrees to score without --allow-seen-course. Picking a model resets the
  // selection to its holdout; the course chips are for changing it from there.
  useEffect(() => {
    setCourseIds(holdout);
  }, [holdout]);
  const scorable = useMemo(
    () => evaluableCourses(courses, provider, holdout, trained),
    [courses, provider, holdout, trained]
  );
  const needsCourses = tool === 'evaluate' && courseIds.length === 0;
  // The chips no longer offer a trained course, but a saved evaluation set can still name one,
  // and so can a selection made before the model was changed. Both routes end at the same
  // refusal, so the run is stopped here instead — naming the courses, which the tool's exit
  // code cannot.
  const seenCourses = useMemo(
    () => (tool === 'evaluate' ? courseIds.filter((id) => trained.includes(id)) : []),
    [tool, courseIds, trained]
  );

  const args = useMemo(
    () =>
      buildArgs({
        tool,
        provider,
        nir,
        register,
        fromS3,
        epochs,
        extra,
        modelName: selectedModel?.name ?? 'unet',
        modelVersion: selectedModel?.version ?? '',
        // A train run's courses are its training set (none means every train-split course);
        // an evaluate run's are whatever the chips say.
        courses: tool === 'train' ? (trainSet?.courseIds ?? []) : courseIds,
        datasetId: dataset?.id ?? '',
        trainSetName: trainSet?.name ?? '',
      }),
    [tool, provider, nir, register, fromS3, epochs, extra, selectedModel, courseIds, trainSet, dataset]
  );

  const run = async () => {
    setBusy(true);
    setResult(null);
    const outcome = await onQueue(tool, args);
    setBusy(false);
    setResult(
      outcome.ok
        ? { ok: true, message: t('mlTrainingPage.queued', { tool }) }
        : {
            ok: false,
            message: [
              outcome.message || t('mlTrainingPage.queueFailed'),
              outcome.httpStatus ? t('mlTrainingPage.httpStatus', { status: outcome.httpStatus }) : null,
            ]
              .filter(Boolean)
              .join(' '),
          }
    );
  };

  return (
    <View style={styles.card} testID="ml-training-run-panel">
      <Text style={styles.fieldLabel}>{t('mlTrainingPage.tool')}</Text>
      <View style={styles.chipRow}>
        {TOOLS.map((value) => (
          <Chip
            key={value}
            label={t(`mlTrainingPage.tools.${value}`)}
            selected={tool === value}
            testID={`ml-training-tool-${value}`}
            onPress={() => setTool(value)}
          />
        ))}
      </View>
      <Text style={styles.hint}>{t(`mlTrainingPage.toolHints.${tool}`)}</Text>

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.provider')}</Text>
      <View style={styles.chipRow}>
        {providers.map((value) => (
          <Chip
            key={value}
            label={value}
            selected={provider === value}
            testID={`ml-training-provider-${value}`}
            onPress={() => setProvider(value)}
          />
        ))}
      </View>

      {tool === 'train' ? (
        <Text style={styles.hint} testID="ml-training-train-data-hint">
          {t('mlTrainingPage.trainDataHint')}
        </Text>
      ) : null}

      {tool === 'train' && trainSets.length > 0 ? (
        <>
          <Text style={styles.fieldLabel}>{t('mlTrainingPage.trainingSet')}</Text>
          <View style={styles.chipRow}>
            <Chip
              label={t('mlTrainingPage.trainingSetAll')}
              selected={trainSet === null}
              testID="ml-training-training-set-all"
              onPress={() => setTrainSetId(null)}
            />
            {trainSets.map((set) => (
              <Chip
                key={set.id}
                label={`${set.name} (${set.courseIds.length})`}
                selected={trainSet?.id === set.id}
                testID={`ml-training-training-set-${set.id}`}
                onPress={() => setTrainSetId(set.id)}
              />
            ))}
          </View>
        </>
      ) : null}

      {tool === 'evaluate' ? (
        <>
          <Text style={styles.fieldLabel}>{t('mlTrainingPage.evaluateModel')}</Text>
          <View style={styles.chipRow}>
            <Chip
              label={t('mlTrainingPage.evaluateClassical')}
              selected={selectedModel === null}
              testID="ml-training-model-classical"
              onPress={() => setModelVersion('')}
            />
            {registered.map((model) => (
              <Chip
                key={model.version}
                label={model.nickname ?? model.version}
                selected={selectedModel?.version === model.version}
                testID={`ml-training-model-${model.version}`}
                onPress={() => setModelVersion(model.version)}
              />
            ))}
          </View>
          {registered.length === 0 ? (
            <Text style={styles.hint}>{t('mlTrainingPage.evaluateNoModels')}</Text>
          ) : null}

          {evalSets.length > 0 ? (
            <>
              <Text style={styles.fieldLabel}>{t('mlTrainingPage.evaluationSet')}</Text>
              <View style={styles.chipRow}>
                {evalSets.map((set) => (
                  <Chip
                    key={set.id}
                    label={`${set.name} (${set.courseIds.length})`}
                    selected={sameCourses(courseIds, set.courseIds)}
                    testID={`ml-training-evaluation-set-${set.id}`}
                    onPress={() => setCourseIds(set.courseIds)}
                  />
                ))}
              </View>
            </>
          ) : null}

          <Text style={styles.fieldLabel}>{t('mlTrainingPage.evaluateCourses')}</Text>
          <View style={styles.chipRow}>
            {holdout.length > 0 ? (
              <Chip
                label={t('mlTrainingPage.evaluateHoldout', { count: holdout.length })}
                selected={holdout.length === courseIds.length && holdout.every((id) => courseIds.includes(id))}
                testID="ml-training-holdout"
                onPress={() => setCourseIds(holdout)}
              />
            ) : null}
            {scorable.map((course) => (
              <Chip
                key={course.courseId}
                label={course.name}
                selected={courseIds.includes(course.courseId)}
                testID={`ml-training-course-${course.courseId}`}
                onPress={() =>
                  setCourseIds((current) =>
                    current.includes(course.courseId)
                      ? current.filter((id) => id !== course.courseId)
                      : [...current, course.courseId]
                  )
                }
              />
            ))}
          </View>
          {trained.length > 0 ? (
            <Text style={styles.hint} testID="ml-training-evaluate-trained-hidden">
              {t('mlTrainingPage.evaluateTrainedHidden', { count: trained.length })}
            </Text>
          ) : null}
          {seenCourses.length > 0 ? (
            <Text style={styles.errorText} testID="ml-training-evaluate-seen-courses">
              {t('mlTrainingPage.evaluateSeenCourses', {
                courses: seenCourses
                  .map((id) => courses.find((course) => course.courseId === id)?.name ?? id)
                  .join(', '),
              })}
            </Text>
          ) : null}
          {needsCourses ? (
            <Text style={styles.errorText} testID="ml-training-evaluate-no-courses">
              {t('mlTrainingPage.evaluateNoCourses')}
            </Text>
          ) : null}
        </>
      ) : null}

      <View style={styles.chipRow}>
        {tool === 'extract' || tool === 'evaluate' ? (
          <Chip
            label={t('mlTrainingPage.fromS3')}
            selected={fromS3}
            testID="ml-training-from-s3"
            onPress={() => setFromS3((v) => !v)}
          />
        ) : null}
        {tool === 'train' ? (
          <Chip
            label={t('mlTrainingPage.nir')}
            selected={nir}
            testID="ml-training-nir"
            onPress={() => setNir((v) => !v)}
          />
        ) : null}
        <Chip
          label={t('mlTrainingPage.register')}
          selected={register}
          testID="ml-training-register"
          onPress={() => setRegister((v) => !v)}
        />
      </View>

      {tool === 'train' ? (
        <>
          <Text style={styles.fieldLabel}>{t('mlTrainingPage.epochs')}</Text>
          <TextInput
            style={styles.input}
            value={epochs}
            onChangeText={setEpochs}
            keyboardType="numeric"
            testID="ml-training-epochs"
          />
        </>
      ) : null}

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.extraArgs')}</Text>
      <TextInput
        style={styles.input}
        value={extra}
        onChangeText={setExtra}
        autoCapitalize="none"
        autoCorrect={false}
        placeholder={t('mlTrainingPage.extraArgsPlaceholder')}
        placeholderTextColor={appColors.text.muted}
        testID="ml-training-extra-args"
      />

      <Text style={styles.fieldLabel}>{t('mlTrainingPage.command')}</Text>
      <Text style={styles.command} testID="ml-training-command">
        {commandLine(tool, args)}
      </Text>

      <Pressable
        style={[styles.runButton, (busy || needsCourses || seenCourses.length > 0) && styles.runButtonDisabled]}
        disabled={busy || needsCourses || seenCourses.length > 0}
        testID="ml-training-run"
        accessibilityRole="button"
        onPress={run}
      >
        <Text style={styles.runButtonText}>
          {busy ? t('mlTrainingPage.queueing') : t('mlTrainingPage.queue')}
        </Text>
      </Pressable>

      {result ? (
        <Text
          style={result.ok ? styles.ok : styles.errorText}
          testID={result.ok ? 'ml-training-queued' : 'ml-training-queue-failed'}
        >
          {result.message}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * The argv for a run.
 *
 * `--dsn` is deliberately absent: geodata rejects it outright, because the caller does not know
 * the worker's tunnel and the worker supplies its own.
 */
export function buildArgs({
  tool,
  provider,
  nir,
  register,
  fromS3,
  epochs,
  extra,
  modelName,
  modelVersion,
  courses,
  datasetId = '',
  trainSetName = '',
}: {
  tool: Tool;
  provider: string;
  nir: boolean;
  register: boolean;
  fromS3: boolean;
  epochs: string;
  extra: string;
  /** The registered model an evaluate run scores; an empty version scores the classical detector alone. */
  modelName: string;
  modelVersion: string;
  /**
   * For evaluate: the courses to score — evaluate.py requires at least one and never guesses.
   * For train: a training set narrowing the train split; none means every train-split course.
   */
  courses: string[];
  /** For train: the registered dataset the run learns from, and the name of the set it narrows to. */
  datasetId?: string;
  trainSetName?: string;
}): string[] {
  const args: string[] = ['--provider', provider];

  if (tool === 'extract') {
    args.push('--out', 'training/data');
    // The tile blocks an acquisition stored at the analysis zoom, rather than a live fetch:
    // the same pixels an inference window is cut from, and no provider quota for pixels that
    // have not changed. Not the whole-course canvas ('s3'), which is stored up to two zoom
    // levels coarser so every hole fits the picture cap — every model up to 2026-09-09 was
    // trained on that and then run on blocks four times its scale. geodata holds the job
    // until every course's acquisition has answered, so the blocks are there when it runs.
    if (fromS3) args.push('--imagery-source', 'stored');
  }

  if (tool === 'train') {
    const parsed = Number.parseInt(epochs, 10);
    if (Number.isFinite(parsed) && parsed > 0) args.push('--epochs', String(parsed));
    // train.py trains the 4-channel model unless told not to; only the opt-out is spelled.
    if (!nir) args.push('--no-nir');
    // Which data, and which size of it by name: the model row records both, so the datasets
    // window can say what was trained on a dataset and the models list can say "quick".
    if (datasetId) args.push('--dataset-id', datasetId);
    if (trainSetName) args.push('--train-set', trainSetName);
  }

  if (tool === 'evaluate') {
    if (modelVersion) args.push('--model-name', modelName, '--model-version', modelVersion);
    // The same shape as the evaluation geodata queues after every training run: scored from
    // the imagery an analysis already stored — no provider credentials, no quota — on whatever
    // accelerator the worker has.
    if (fromS3) args.push('--imagery-source', 'stored');
    args.push('--device', 'auto');
  }

  if (register) args.push('--register');

  if (tool === 'train' || tool === 'evaluate') {
    for (const course of courses) args.push('--course', course);
  }

  // Split on whitespace, the way a shell would. Quoting is not supported on purpose: an
  // argument needing quotes is a sign this should be run from a terminal instead.
  const words = extra.trim().split(/\s+/).filter((word) => word.length > 0);
  args.push(...words);

  return args;
}

/**
 * The dataset a training run for this provider learns from: the newest registered one. One per
 * provider is the intended shape — sizes are course sets over it, not separate cuts — so newest
 * is the one whose split the sets were built from.
 */
export function newestDatasetFor(datasets: MlDataset[], provider: string): MlDataset | null {
  return (
    datasets
      .filter((dataset) => describeFilter(dataset.filterJson).provider === provider)
      .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))[0] ?? null
  );
}

/**
 * What an evaluate run can be pointed at: every course with a stored image under the provider
 * (the only ones `--imagery-source stored` can read), plus the model's holdout courses even when
 * the training-data list does not carry them — that set is the one the run exists for.
 */
export function evaluableCourses(
  courses: MlTrainingCourse[],
  provider: string,
  holdout: string[],
  trained: string[] = []
): { courseId: string; name: string }[] {
  // A course the checkpoint trained on is not offered at all. evaluate.py refuses the whole run
  // when one is named -- all of it, not just that course -- so picking one costs a queued job,
  // a worker slot and a round trip to find out what the registry row already said.
  const seen = new Set(trained);
  const stored = courses
    .filter((course) => course.images.some((image) => image.provider === provider))
    .filter((course) => !seen.has(course.courseId))
    .map((course) => ({ courseId: course.courseId, name: course.name }));
  const listed = new Set(stored.map((course) => course.courseId));
  const unlisted = holdout
    .filter((id) => !listed.has(id) && !seen.has(id))
    .map((id) => ({ courseId: id, name: courses.find((course) => course.courseId === id)?.name ?? id }));
  return [...stored, ...unlisted];
}

/** Whether two course lists name the same courses, in any order. */
function sameCourses(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/**
 * The named course sets: what there is, and a form to make or change one out of the
 * training-data list.
 *
 * A set is built from courses by name rather than by id, with each course's mapped-feature
 * count beside it, because "the three richest" is how a training set is actually chosen. The
 * size line on each set — courses and mapped features — is the number the run form cannot show
 * on a chip.
 */
function CourseSetsSection({
  sets,
  courses,
  datasets,
  onSave,
  onRemove,
  t,
}: {
  sets: LoadState<MlCourseSets>;
  courses: MlTrainingCourse[];
  /** The registry's datasets; the ones carrying a split are what a size is built from. */
  datasets: MlDataset[];
  onSave: (id: string | null, draft: MlCourseSetDraft) => Promise<CourseSetWriteResult>;
  onRemove: (id: string) => Promise<CourseSetWriteResult>;
  t: Translate;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<MlCourseSetPurpose>('train');
  const [courseIds, setCourseIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  // Eligible courses first, then by name: the ones a training set is made of come to the top.
  const pickable = useMemo(
    () =>
      [...courses].sort((a, b) =>
        a.eligible === b.eligible ? a.name.localeCompare(b.name) : a.eligible ? -1 : 1
      ),
    [courses]
  );
  const nameOf = (id: string) => courses.find((course) => course.courseId === id)?.name ?? id;
  const labelsOf = (ids: string[]) =>
    ids.reduce((sum, id) => sum + (courses.find((course) => course.courseId === id)?.totalLabels ?? 0), 0);

  // Sizes are built from a dataset's recorded split, so training and evaluation stay disjoint
  // by construction: a training set draws on the train split only, an evaluation set on the
  // holdout only. The newest dataset carrying a split is the default; a row registered before
  // the split was recorded cannot build anything, and says so.
  const withSplit = useMemo(
    () => datasets.filter((dataset) => describeFilter(dataset.filterJson).trainCourses.length > 0),
    [datasets]
  );
  const [buildDatasetId, setBuildDatasetId] = useState<string | null>(null);
  const buildDataset = withSplit.find((dataset) => dataset.id === buildDatasetId) ?? withSplit[0] ?? null;
  const split = useMemo(() => describeFilter(buildDataset?.filterJson ?? null), [buildDataset]);
  const [buildSize, setBuildSize] = useState('3');
  const richness = (id: string) => courses.find((course) => course.courseId === id)?.totalLabels ?? null;

  const build = () => {
    const size = Number.parseInt(buildSize, 10);
    if (!buildDataset || !Number.isFinite(size) || size <= 0) return;
    const picked = pickBySize(purpose === 'train' ? split.trainCourses : split.holdoutCourses, size, richness);
    setCourseIds(picked);
    if (name.trim().length === 0) setName(`${purpose === 'train' ? 'train' : 'eval'}-${picked.length}`);
  };

  // The two rules, checked against the dataset the form is building from: a training set must
  // not reach into its holdout, an evaluation set must not reach into its train split. Either
  // way the evaluation would then score courses the model had seen. evaluate.py refuses that at
  // scoring time; refusing here is earlier and says why.
  const crossing = buildDataset
    ? courseIds.filter((id) => (purpose === 'train' ? split.holdoutCourses : split.trainCourses).includes(id))
    : [];

  const startNew = () => {
    setEditingId(null);
    setName('');
    setPurpose('train');
    setCourseIds([]);
    setNotes('');
    setMessage(null);
  };
  const startEdit = (set: MlCourseSet) => {
    setEditingId(set.id);
    setName(set.name);
    setPurpose(set.purpose);
    setCourseIds(set.courseIds);
    setNotes(set.notes ?? '');
    setMessage(null);
  };

  const problem =
    name.trim().length === 0
      ? t('mlTrainingPage.sets.needsName')
      : courseIds.length === 0
        ? t('mlTrainingPage.sets.needsCourses')
        : crossing.length > 0
          ? t(purpose === 'train' ? 'mlTrainingPage.sets.trainHasHoldout' : 'mlTrainingPage.sets.evaluateHasTrain', {
              count: crossing.length,
            })
          : null;

  const save = async () => {
    if (problem) return;
    setBusy(true);
    setMessage(null);
    const outcome = await onSave(editingId, { name: name.trim(), purpose, courseIds, notes: notes.trim() });
    setBusy(false);
    if (outcome.ok) {
      setMessage({ ok: true, text: t('mlTrainingPage.sets.saved') });
      if (editingId === null) {
        setName('');
        setCourseIds([]);
        setNotes('');
      }
    } else {
      setMessage({ ok: false, text: outcome.message || t('mlTrainingPage.sets.saveFailed') });
    }
  };

  const remove = async (set: MlCourseSet) => {
    setBusy(true);
    setMessage(null);
    const outcome = await onRemove(set.id);
    setBusy(false);
    if (outcome.ok) {
      setMessage({ ok: true, text: t('mlTrainingPage.sets.deleted') });
      if (editingId === set.id) startNew();
    } else {
      setMessage({ ok: false, text: outcome.message || t('mlTrainingPage.sets.deleteFailed') });
    }
  };

  return (
    <View>
      <Text style={styles.hint}>{t('mlTrainingPage.sets.legend')}</Text>

      {sets.status === 'loading' ? (
        <Text style={styles.paragraph}>{t('mlTrainingPage.loading')}</Text>
      ) : sets.status !== 'ready' ? (
        <Text style={styles.errorText}>{t('mlTrainingPage.sets.failed')}</Text>
      ) : sets.sets.length === 0 ? (
        <Text style={styles.paragraph} testID="ml-training-sets-empty">
          {t('mlTrainingPage.sets.empty')}
        </Text>
      ) : (
        sets.sets.map((set) => (
          <View key={set.id} style={styles.card} testID={`ml-training-set-${set.id}`}>
            <View style={styles.cardHeaderRow}>
              <Text style={styles.cardTitle}>{set.name}</Text>
              <Text style={styles.badgeStatus}>{t(`mlTrainingPage.sets.purposes.${set.purpose}`)}</Text>
            </View>
            <Text style={styles.meta}>
              {t('mlTrainingPage.sets.size', { courses: set.courseIds.length, labels: labelsOf(set.courseIds) })}
            </Text>
            <Text style={styles.meta}>{set.courseIds.map(nameOf).join('  ·  ')}</Text>
            {set.notes ? <Text style={styles.meta}>{set.notes}</Text> : null}
            <View style={styles.tagRow}>
              <SmallButton
                label={t('mlTrainingPage.sets.edit')}
                testID={`ml-training-set-edit-${set.id}`}
                onPress={() => startEdit(set)}
              />
              <SmallButton
                label={t('mlTrainingPage.sets.delete')}
                testID={`ml-training-set-delete-${set.id}`}
                onPress={() => {
                  void remove(set);
                }}
              />
            </View>
          </View>
        ))
      )}

      <View style={styles.card} testID="ml-training-set-form">
        <Text style={styles.cardTitle}>{editingId ? name : t('mlTrainingPage.sets.new')}</Text>

        <Text style={styles.fieldLabel}>{t('mlTrainingPage.sets.name')}</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          autoCapitalize="none"
          autoCorrect={false}
          placeholder={t('mlTrainingPage.sets.namePlaceholder')}
          placeholderTextColor={appColors.text.muted}
          testID="ml-training-set-name"
        />

        <Text style={styles.fieldLabel}>{t('mlTrainingPage.sets.purpose')}</Text>
        <View style={styles.chipRow}>
          {(['train', 'evaluate'] as const).map((value) => (
            <Chip
              key={value}
              label={t(`mlTrainingPage.sets.purposes.${value}`)}
              selected={purpose === value}
              testID={`ml-training-set-purpose-${value}`}
              onPress={() => setPurpose(value)}
            />
          ))}
        </View>

        <Text style={styles.fieldLabel}>{t('mlTrainingPage.sets.buildHeading')}</Text>
        {withSplit.length === 0 ? (
          <Text style={styles.hint} testID="ml-training-set-build-none">
            {t('mlTrainingPage.sets.buildDatasetNone')}
          </Text>
        ) : (
          <>
            <View style={styles.chipRow}>
              {withSplit.map((candidate) => (
                <Chip
                  key={candidate.id}
                  label={candidate.description}
                  selected={buildDataset?.id === candidate.id}
                  testID={`ml-training-set-build-dataset-${candidate.id}`}
                  onPress={() => setBuildDatasetId(candidate.id)}
                />
              ))}
            </View>
            <Text style={styles.hint} testID="ml-training-set-build-hint">
              {purpose === 'train'
                ? t('mlTrainingPage.sets.buildTrainHint', { count: split.trainCourses.length })
                : t('mlTrainingPage.sets.buildEvaluateHint', { count: split.holdoutCourses.length })}
            </Text>
            <View style={styles.chipRow}>
              <TextInput
                style={styles.input}
                value={buildSize}
                onChangeText={setBuildSize}
                keyboardType="numeric"
                testID="ml-training-set-build-size"
              />
              <SmallButton
                label={t('mlTrainingPage.sets.build', { count: buildSize })}
                testID="ml-training-set-build"
                onPress={build}
              />
            </View>
          </>
        )}

        <Text style={styles.fieldLabel}>{t('mlTrainingPage.sets.courses')}</Text>
        <View style={styles.chipRow}>
          <Chip
            label={t('mlTrainingPage.sets.allEligible')}
            selected={false}
            testID="ml-training-set-all-eligible"
            onPress={() => setCourseIds(pickable.filter((course) => course.eligible).map((course) => course.courseId))}
          />
          <Chip
            label={t('mlTrainingPage.sets.clear')}
            selected={false}
            testID="ml-training-set-clear"
            onPress={() => setCourseIds([])}
          />
        </View>
        <View style={styles.chipRow}>
          {pickable.map((course) => (
            <Chip
              key={course.courseId}
              label={`${course.name || t('mlTrainingPage.unnamedCourse')} · ${course.totalLabels}`}
              selected={courseIds.includes(course.courseId)}
              testID={`ml-training-set-course-${course.courseId}`}
              onPress={() =>
                setCourseIds((current) =>
                  current.includes(course.courseId)
                    ? current.filter((id) => id !== course.courseId)
                    : [...current, course.courseId]
                )
              }
            />
          ))}
        </View>
        <Text style={styles.meta}>
          {t('mlTrainingPage.sets.size', { courses: courseIds.length, labels: labelsOf(courseIds) })}
        </Text>

        <Text style={styles.fieldLabel}>{t('mlTrainingPage.sets.notes')}</Text>
        <TextInput
          style={styles.input}
          value={notes}
          onChangeText={setNotes}
          testID="ml-training-set-notes"
        />

        <Pressable
          style={[styles.runButton, (busy || problem !== null) && styles.runButtonDisabled]}
          disabled={busy || problem !== null}
          testID="ml-training-set-save"
          accessibilityRole="button"
          onPress={() => {
            void save();
          }}
        >
          <Text style={styles.runButtonText}>
            {busy ? t('mlTrainingPage.sets.saving') : t('mlTrainingPage.sets.save')}
          </Text>
        </Pressable>
        {editingId ? (
          <SmallButton label={t('mlTrainingPage.sets.cancel')} testID="ml-training-set-cancel" onPress={startNew} />
        ) : null}
        {problem ? <Text style={styles.hint}>{problem}</Text> : null}
        {message ? (
          <Text
            style={message.ok ? styles.ok : styles.errorText}
            testID={message.ok ? 'ml-training-set-saved' : 'ml-training-set-failed'}
          >
            {message.text}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

/** The command as it will run on the worker, for the developer to recognise or copy. */
export function commandLine(tool: Tool, args: string[]): string {
  const script =
    tool === 'extract' ? 'extract_dataset.py' : tool === 'tune' ? 'tune_thresholds.py' : `${tool}.py`;
  return `python3 training/${script} ${args.join(' ')}`;
}

function ThresholdSummary({
  thresholds,
  eligible,
  total,
  t,
}: {
  thresholds: MlThresholds;
  eligible: number;
  total: number;
  t: Translate;
}) {
  return (
    <View style={styles.card} testID="ml-training-threshold-summary">
      <Text style={styles.cardTitle}>
        {t('mlTrainingPage.eligibleCount', { eligible, total })}
      </Text>
      <Text style={styles.meta}>
        {t('mlTrainingPage.thresholds', {
          bunkers: thresholds.minBunkers,
          fairways: thresholds.minFairways,
        })}
      </Text>
    </View>
  );
}

function CourseCard({ course, t }: { course: MlTrainingCourse; t: Translate }) {
  return (
    <View style={styles.card} testID={`ml-training-course-${course.courseId}`}>
      <View style={styles.cardHeaderRow}>
        <Text style={styles.cardTitle}>{course.name || t('mlTrainingPage.unnamedCourse')}</Text>
        <Text style={course.eligible ? styles.badgeEligible : styles.badgeShort}>
          {course.eligible ? t('mlTrainingPage.eligible') : t('mlTrainingPage.notEligible')}
        </Text>
      </View>

      <Text style={styles.meta}>
        {LABEL_KINDS.map((kind) => `${kind} ${course.labelCounts[kind] ?? 0}`).join('  ·  ')}
      </Text>

      {course.shortfall.length > 0 ? (
        <Text style={styles.warning} testID={`ml-training-shortfall-${course.courseId}`}>
          {course.shortfall
            .map((s) =>
              t('mlTrainingPage.shortfall', {
                kind: s.kind,
                missing: s.required - s.has,
                required: s.required,
              })
            )
            .join('  ·  ')}
        </Text>
      ) : null}

      <View style={styles.tagRow}>
        {course.osmUrl ? (
          <LinkTag label={t('mlTrainingPage.openInOsm')} url={course.osmUrl} testID={`ml-training-osm-${course.courseId}`} />
        ) : (
          <Text style={styles.mutedTag} testID={`ml-training-no-osm-${course.courseId}`}>
            {t('mlTrainingPage.noOsm')}
          </Text>
        )}
        {course.images.map((image) => (
          <LinkTag
            key={`${image.provider}-${image.kind}`}
            label={`${image.provider} · ${image.kind}`}
            url={image.url}
            testID={`ml-training-image-${course.courseId}-${image.provider}-${image.kind}`}
          />
        ))}
        {course.images.length === 0 ? (
          <Text style={styles.mutedTag}>{t('mlTrainingPage.noImages')}</Text>
        ) : null}
      </View>
    </View>
  );
}

// The run history stores UTC instants and the API hands them over as ISO strings
// ("2026-09-05T17:17:53.726689Z"). The trailing Z is easy to read past, and a run history read
// as local time is a run history read wrong, so spell the zone out.
function formatRequestedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return `${parsed.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

function JobCard({ job, t }: { job: MlJob; t: Translate }) {
  const failed = job.exitCode !== null && job.exitCode !== 0;
  // A tuning run prints a few hundred lines of coordinate descent, and the widest of them —
  // the crop-id list, the PROVIDER_THRESHOLDS block — are far wider than the card. Rendered
  // inline they pushed the run history into a wall of monospace with its right-hand end cut
  // off, so the one thing worth reading (the overfit warning, the exit code) was buried in it.
  // Behind a button the history is a list of runs again, and the log gets a window that can
  // scroll both ways.
  const [outputOpen, setOutputOpen] = useState(false);
  // Off by default: the aligned view is the right one for a run that produced a table. It is the
  // wrong one for a run that produced one long sentence, so it is one tap away rather than fixed.
  const [wrapped, setWrapped] = useState(false);
  const toolName = t(`mlTrainingPage.tools.${job.kind}`, {});

  return (
    <View style={styles.card} testID={`ml-training-job-${job.jobId}`}>
      <View style={styles.cardHeaderRow}>
        <Text style={styles.cardTitle}>{toolName}</Text>
        {/* "running" is the worker's heartbeat, not the row: a queued job a worker has in its
            hands looks exactly like one nobody has picked up, and a training run spends twenty
            minutes in that state. The row still says queued everywhere it is stored. */}
        <Text
          style={job.running ? styles.badgeRunning : failed ? styles.badgeShort : styles.badgeStatus}
          testID={`ml-training-job-status-${job.jobId}`}
        >
          {job.running ? t('mlTrainingPage.jobRunning') : job.status}
        </Text>
        {job.workerId ? <Text style={styles.meta}>{job.workerId}</Text> : null}
      </View>
      <Text style={styles.command}>{job.args.join(' ') || '—'}</Text>
      <Text style={styles.meta}>
        {formatRequestedAt(job.requestedAt)}
        {job.durationSeconds !== null ? `  ·  ${job.durationSeconds}s` : ''}
        {job.exitCode !== null ? `  ·  exit ${job.exitCode}` : ''}
      </Text>
      {job.error ? <Text style={styles.errorText}>{job.error}</Text> : null}
      {job.outputTail ? (
        <Pressable
          testID={`ml-training-output-open-${job.jobId}`}
          accessibilityRole="button"
          style={styles.outputButton}
          onPress={() => setOutputOpen(true)}
        >
          <Text style={styles.outputButtonText}>{t('mlTrainingPage.showOutput')}</Text>
        </Pressable>
      ) : null}

      <Modal
        visible={outputOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setOutputOpen(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setOutputOpen(false)}>
          {/* Swallows the tap so clicking the log itself does not dismiss the window. */}
          <Pressable style={styles.outputCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('mlTrainingPage.outputTitle', { tool: toolName })}
              </Text>
              <View style={styles.outputHeaderActions}>
                <Pressable
                  testID={`ml-training-output-wrap-${job.jobId}`}
                  accessibilityRole="button"
                  aria-pressed={wrapped}
                  hitSlop={8}
                  onPress={() => setWrapped((on) => !on)}
                >
                  <Text style={[styles.modalClose, wrapped && styles.outputWrapOn]}>
                    {t('mlTrainingPage.outputWrap')}
                  </Text>
                </Pressable>
                <Pressable
                  testID={`ml-training-output-close-${job.jobId}`}
                  accessibilityRole="button"
                  accessibilityLabel={t('menu.close')}
                  hitSlop={8}
                  onPress={() => setOutputOpen(false)}
                >
                  <Text style={styles.modalClose}>{t('menu.close')}</Text>
                </Pressable>
              </View>
            </View>
            {/* Two scrollers by default, and both are needed: the log is long AND its widest
                lines are wider than any window. Wrapping folds a 200-character crop-id list into
                a paragraph and loses the column alignment the IoU table is read by — which is
                why it is a choice and not the default. A tool's error message is the other case:
                one long prose line whose end is the whole point, and hunting for it by dragging
                a log sideways is how a reader gives up before the sentence does. */}
            <ScrollView style={styles.outputScroll}>
              {wrapped ? (
                <Text testID={`ml-training-output-text-${job.jobId}`} style={styles.output}>
                  {job.outputTail}
                </Text>
              ) : (
                <ScrollView horizontal testID={`ml-training-output-sideways-${job.jobId}`}>
                  <Text testID={`ml-training-output-text-${job.jobId}`} style={styles.output}>
                    {job.outputTail}
                  </Text>
                </ScrollView>
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      // RN-web drops accessibilityState, so selection has to reach the DOM as aria-*.
      aria-pressed={selected}
      style={[styles.chip, selected && styles.chipSelected]}
      testID={testID}
      onPress={onPress}
    >
      <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{label}</Text>
    </Pressable>
  );
}

function LinkTag({ label, url, testID }: { label: string; url: string; testID: string }) {
  return (
    <Pressable
      accessibilityRole="link"
      style={styles.linkTag}
      testID={testID}
      onPress={() => {
        void Linking.openURL(url);
      }}
    >
      <Text style={styles.linkTagText}>{label}</Text>
    </Pressable>
  );
}

function SmallButton({ onPress, label, testID }: { onPress: () => void; label: string; testID: string }) {
  return (
    <Pressable accessibilityRole="button" style={styles.refreshButton} testID={testID} onPress={onPress}>
      <Text style={styles.refreshButtonText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  refreshRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  sectionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 14 },
  sectionButton: {
    // Wide enough for a heading on one line, narrow enough that four of them tile on a laptop.
    minWidth: 190,
    flexGrow: 1,
    flexBasis: 190,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 4,
  },
  sectionButtonLabel: { color: appColors.text.primary, fontWeight: '700', fontSize: 14 },
  sectionButtonDetail: { color: appColors.text.secondary, fontSize: 12 },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  refreshButtonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
  card: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    padding: 12,
    marginBottom: 10,
    gap: 6,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardTitle: { color: appColors.text.primary, fontWeight: '700', fontSize: 14 },
  meta: { color: appColors.text.secondary, fontSize: 12 },
  hint: { color: appColors.text.muted, fontSize: 11, lineHeight: 15 },
  fieldLabel: { color: appColors.text.primary, fontWeight: '600', fontSize: 12, marginTop: 6 },
  chipRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.app,
  },
  chipSelected: { borderColor: appColors.accent.primary, backgroundColor: appColors.background.panel },
  chipText: { color: appColors.text.secondary, fontSize: 12 },
  chipTextSelected: { color: appColors.accent.primary, fontWeight: '700' },
  input: {
    borderWidth: 1,
    borderColor: appColors.border.default,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    color: appColors.text.primary,
    backgroundColor: appColors.background.app,
    fontSize: 12,
  },
  command: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontFamily: appFonts.systemMono,
    backgroundColor: appColors.background.app,
    borderRadius: 6,
    padding: 8,
  },
  output: {
    // Not text.muted. This is the content the window exists to show, and muted is the palette's
    // faintest ink: on the yardage-book skin it lands at 3.88:1 against background.app, under
    // WCAG AA, in 10px mono — a run's error message read as a pale smudge. See
    // theme/skinContrast.test.ts, which now pins this pair.
    color: appColors.text.secondary,
    fontSize: 11,
    lineHeight: 16,
    fontFamily: appFonts.systemMono,
    backgroundColor: appColors.background.app,
    borderRadius: 6,
    padding: 8,
  },
  outputButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
  },
  outputButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.6),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  sectionCard: {
    width: '100%',
    maxWidth: 900,
    maxHeight: '85%',
    borderRadius: 12,
    backgroundColor: appColors.background.panel,
    padding: 12,
  },
  sectionScroll: { flexGrow: 0 },
  sectionScrollContent: { paddingBottom: 4 },
  outputCard: {
    width: '100%',
    maxWidth: 900,
    // A share of the window rather than a fixed height: the point of the window is that the
    // log has room, and on a laptop that is most of the screen.
    maxHeight: '85%',
    borderRadius: 12,
    backgroundColor: appColors.background.panel,
    padding: 12,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  modalTitle: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
    flexShrink: 1,
  },
  modalClose: {
    color: appColors.text.secondary,
    fontSize: 13,
    fontWeight: '600',
  },
  outputScroll: {
    borderRadius: 6,
    backgroundColor: appColors.background.app,
  },
  outputHeaderActions: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  outputWrapOn: { color: appColors.accent.primary, fontWeight: '700' },
  runButton: {
    marginTop: 8,
    paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: appColors.accent.primary,
    alignItems: 'center',
  },
  runButtonDisabled: { opacity: 0.5 },
  runButtonText: { color: appColors.background.app, fontWeight: '700', fontSize: 13 },
  warning: { color: appColors.accent.goldMuted, fontSize: 12 },
  ok: { color: appColors.status.successStrong, fontSize: 12 },
  errorText: { color: appColors.status.errorTextMuted, fontSize: 12 },
  tagRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 2 },
  linkTag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  linkTagText: { color: appColors.accent.primary, fontSize: 11, fontWeight: '600' },
  mutedTag: { color: appColors.text.muted, fontSize: 11, paddingVertical: 3 },
  badgeEligible: { color: appColors.status.successStrong, fontSize: 11, fontWeight: '700' },
  badgeShort: { color: appColors.accent.goldMuted, fontSize: 11, fontWeight: '700' },
  badgeStatus: { color: appColors.text.secondary, fontSize: 11, fontWeight: '700' },
  badgeRunning: { color: appColors.accent.primary, fontSize: 11, fontWeight: '700' },
});
